import { useEffect, useRef, useState } from "react";

/**
 * Scroll infinito: busca a próxima página quando o sentinela (o fim da lista)
 * chega perto da tela. O observador é refeito ao terminar cada página: se o
 * fim continua à vista (página curta), ele avisa de novo e a busca segue.
 * Com erro (`paused`), para de buscar sozinho até a pessoa tentar de novo.
 * Sem `IntersectionObserver` (jsdom, navegador antigo), `supported` é falso e
 * quem chama mostra o botão.
 */
export function useLoadMoreOnScroll({
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  paused = false,
  rootMargin = "320px",
}: {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => unknown;
  paused?: boolean;
  rootMargin?: string;
}) {
  const [node, setNode] = useState<HTMLElement | null>(null);
  const supported = typeof IntersectionObserver !== "undefined";
  const fetchRef = useRef(fetchNextPage);
  fetchRef.current = fetchNextPage;
  const active = hasNextPage && !isFetchingNextPage && !paused;

  useEffect(() => {
    if (!node || !supported || !active) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          void fetchRef.current();
        }
      },
      { rootMargin }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [node, supported, active, rootMargin]);

  return { sentinelRef: setNode, supported };
}
